import React, { FC, Fragment } from 'react';
import { AiOutlineClose } from 'react-icons/ai';
import { FaFilter } from 'react-icons/fa';
import { BsSearch } from 'react-icons/bs';
import { RouteComponentProps, withRouter } from 'react-router';
import styled from 'styled-components';
import { color, height, space, width } from 'styled-system';
import { AuthConsumer } from '../../containers/shared/auth/AuthContext';
import { StyledSystemProps } from '../../theme/StyledSystemProps';
import { Flex, CollapsibleFilters, StyledInput, Modal, Box, NotificationsIcon } from '../../components/shared';
import {
  ALL_INSTITUTION_PORTAL_USERS,
  ALL_INSTITUTION_TYPES,
  ALL_LOCAL_MUNICIPALITIES,
  ALL_PROVINCES,
  ALL_REGISTRATION_STATUSES,
  FILTERED_ECD_DISTRICTS,
  FILTERED_INSTITUTION_DISTRICTS,
  FILTERED_INSTITUTION_PROVINCES,
  GET_MINISTRIES,
  GET_REGISTRATION_OFFICES,
} from '../../graphql';
import { BreadcrumbsArray } from './Breadcrumbs';
import { Breadcrumbs, Button } from './index';
import HamburgerIcon from './Sidebar/HamburgerIcon';
import { MobileHamburgerCont } from './Sidebar/StyledElements';
import { PortalDropdown } from './PortalDropdown';
import { theme } from '../../theme';
import NotificationsDropDown from './NotificationsDropDown';

type HeaderProps = RouteComponentProps &
  StyledSystemProps & {
    breadcrumbs: BreadcrumbsArray;
    color?: string;
    context: any;
    isMobile?: boolean;
    isTablet?: boolean;
    isWeb?: boolean;
    size?: number;
  };

type FilterState = {
  districtId?: string;
  ministryId?: string;
  emisNumber?: string;
  filterType?: string;
  name?: string;
  provinceId?: string;
  status?: string;
  typeId?: string;
  districtMunicipalityId?: string;
  duplicateAction?: string;
  ecdServiceType?: string;
  localMunicipalityId?: string;
  registrationExpiryStartDate?: string;
  registrationExpiryEndDate?: string;
  registrationOfficeId?: string;
  registrationStatusId?: string;
  userId?: string;
  wardID?: number;
};

const initialFilterState = {
  districtId: undefined,
  ministryId: undefined,
  emisNumber: undefined,
  filterType: undefined,
  name: undefined,
  provinceId: undefined,
  status: undefined,
  typeId: undefined,
  districtMunicipalityId: undefined,
  duplicateAction: undefined,
  ecdServiceType: undefined,
  localMunicipalityId: undefined,
  registrationExpiryStartDate: undefined,
  registrationExpiryEndDate: undefined,
  registrationOfficeId: undefined,
  registrationStatusId: undefined,
  userId: undefined,
  wardID: undefined,
};

const HeaderCont = styled.div<HeaderProps>`
  ${space};
  ${width};
  color: ${props => (props.isWeb ? color : props.theme.colors.primary)};
  ${height};
  display: flex;
  align-items: center;
  box-sizing: border-box;
  justify-content: ${props => (props.isWeb ? 'space-between' : 'flex-start')};
  background: ${props => (props.isWeb ? props.theme.colors.primary : props.theme.colors.white)};
  box-shadow: ${props => !props.isWeb && '0 2px 4px 0 rgba(0, 0, 0, 0.2), 0 6px 20px 0 rgba(0, 0, 0, 0.19)'};
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const BreadcrumbCont = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const CollapsibleFilterWrapper = styled.div`
  background-color: ${theme.colors.transparentPrimary};
  color: ${theme.colors.white};
  display: flex;
  flex-direction: row;
  font-weight: 400;
  justify-content: flex-end;
  padding: 10px 0;
  top: 0;
  width: 100%;

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-moz-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-webkit-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-o-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  @-ms-keyframes fadeIn {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const FILTER_OPTIONS = [
  {
    label: 'District',
    value: 'districtId',
    query: { query: FILTERED_INSTITUTION_DISTRICTS, result: 'districts', variables: { input: {} } },
  },
  {
    label: 'Ministry',
    value: 'ministryId',
    query: { query: GET_MINISTRIES, result: 'ministries', variables: { input: {} } },
  },
  {
    label: 'Province',
    value: 'provinceId',
    query: { query: FILTERED_INSTITUTION_PROVINCES, result: 'provinces', variables: { input: {} } },
  },
  {
    label: 'Status',
    value: 'status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Archived', value: 'ARCHIVED' },
    ],
  },

  {
    label: 'Type',
    value: 'typeId',
    query: { query: ALL_INSTITUTION_TYPES, result: 'institutionTypes', variables: { input: {} } },
  },
];

const INSTITUTION_PORTAL_FILTER_OPTIONS = [
  {
    label: 'Education District',
    value: 'districtId',
    query: {
      query: FILTERED_ECD_DISTRICTS,
      result: 'districts',
      variables: { input: {} },
    },
  },
  {
    label: 'ECD Service Type',
    value: 'ecdServiceType',
    options: [
      {
        label: 'After school service partial care facility',
        value: 'After school service partial care facility',
      },
      { label: 'ECD centre', value: 'ECD centre' },
      { label: 'Child-minder/day mother', value: 'Child-minder/day mother' },
      { label: 'Mobile ECD programme', value: 'Mobile ECD programme' },
      { label: 'Playgroup', value: 'Playgroup' },
      { label: 'Parental support programme', value: 'Parental support programme' },
      { label: 'Toy library', value: 'Toy library' },
    ],
  },
  {
    label: 'Name',
    value: 'name',
    isText: true,
  },
  {
    label: 'Province',
    value: 'provinceId',
    query: { query: ALL_PROVINCES, result: 'provinces', variables: { input: {} }, notSearchable: true },
  },

  {
    label: 'Institution Type',
    value: 'typeId',
    query: {
      query: ALL_INSTITUTION_TYPES,
      result: 'institutionTypes',
      variables: { input: {} },
      notSearchable: true,
    },
  },
  {
    label: 'Local Municipality',
    value: 'localMunicipalityId',
    query: {
      query: ALL_LOCAL_MUNICIPALITIES,
      result: 'municipalities',
      variables: { input: {} },
    },
  },

  {
    label: 'Registration Office',
    value: 'registrationOfficeId',
    query: {
      query: GET_REGISTRATION_OFFICES,
      result: 'institutions',
      variables: { input: { first: 15 } },
      resultOptionFunction: (data: any): { label: string; value: string } => {
        return {
          label: data.institution ? data.institution.name : data.serviceProvider ? data.serviceProvider.name : '',
          value: data.id,
        };
      },
    },
  },

  {
    label: 'Registration Status',
    value: 'registrationStatusId',
    query: {
      query: ALL_REGISTRATION_STATUSES,
      result: 'registrationStatuses',
      variables: { input: { enabled: true } },
      notSearchable: true,
    },
  },
  {
    label: 'Registration Level',
    value: 'registrationLevel',
    options: [
      { label: 'Bronze', value: 'BRONZE' },
      { label: 'Gold', value: 'GOLD' },
      { label: 'Silver', value: 'SILVER' },
    ],
  },
  {
    label: 'Registration Expiry Date',
    value: 'registrationExpiryDate',
    isDateRange: true,
    startDate: 'registrationExpiryStartDate',
    endDate: 'registrationExpiryEndDate',
  },
  {
    label: 'Registration Progress',
    value: 'registration',
    options: [
      { label: 'Delayed', value: 'Delayed' },
      { label: 'Declined', value: 'Declined' },
      { label: 'Expired', value: 'Expired' },
      { label: 'On track', value: 'On track' },
      { label: 'Registered', value: 'Registered' },
      { label: 'Unregistered', value: 'Register' },
    ],
  },
  {
    label: 'Status',
    value: 'status',
    options: [
      { label: 'Active', value: 'ACTIVE' },
      { label: 'Archived', value: 'ARCHIVED' },
    ],
  },
  {
    label: 'User',
    value: 'userId',
    query: { query: ALL_INSTITUTION_PORTAL_USERS, result: 'users', variables: { input: {} } },
  },
  {
    label: 'Ward',
    value: 'wardID',
    isNumber: true,
  },
];

const Header: FC<HeaderProps> = props => {
  const { breadcrumbs, match, isWeb, context } = props;
  const [expandFilter, setExpandFilter] = React.useState<boolean>(false);
  const { setFilterState, filterState } = context;
  const [openSearch, setOpenSearch] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState<string>('');
  const [state, setState] = React.useState<FilterState>(initialFilterState);

  const _changeHandler = ({ value }: { value: string | Date | number | null }, stateKey?: string) => {
    const newState = Object.assign({}, state);
    // clear all values when filterTypeChanges
    Object.keys(newState).forEach((key: string) => {
      if (key === stateKey) {
        newState[key] = value;
      }
    });
    setState(newState);
  };

  const url = () => {
    const path = match.url;
    if (path.startsWith('/service-provider')) {
      return '/service-provider';
    }
    if (path.startsWith('/funder')) {
      return '/funder';
    }
    if (path.startsWith('/institution')) {
      return '/institution';
    }
    return '/admin';
  };

  return (
    <HeaderCont {...props}>
      {!isWeb && (
        <Fragment>
          <MobileHamburgerCont open={context.openMobileDrawer}>
            <HamburgerIcon
              onClick={context.toggleMobileDrawer}
              open={context.openMobileDrawer}
              color={theme.colors.primary}
              isWeb={isWeb}
            />
          </MobileHamburgerCont>
          <Flex direction="row" size="1" justify="center" marginRight="10px" />
        </Fragment>
      )}

      {isWeb && (
        <BreadcrumbCont>
          <Breadcrumbs breadcrumbs={breadcrumbs} />
        </BreadcrumbCont>
      )}

      {!isWeb && (
        <Flex direction="row" size="8" justify="flex-start" width="100%" pl={1}>
          {breadcrumbs.length && breadcrumbs[breadcrumbs.length - 1].title}
        </Flex>
      )}

      {!isWeb && match.url.includes('/manage/institutions') && (
        <Flex direction="row" size="8" justify="flex-end" alignItems="center" pl={4}>
          {openSearch && (
            <Flex size="2" direction="row" justify="flex-end" alignItems="center">
              <Flex size="2" direction="row" justify="flex-end">
                <StyledInput
                  id="searchTerm"
                  aria-label="Search"
                  placeholder="Type Name/EMIS..."
                  height={30}
                  onChange={(event: any) => {
                    setFilterState({ ...state, name: event.target.value });
                  }}
                />
              </Flex>
            </Flex>
          )}
          {openSearch && (
            <Flex size="0.1" direction="row" justify="flex-end">
              <AiOutlineClose
                onClick={() => {
                  setOpenSearch(false);
                  setFilterState({ ...state, name: undefined });
                }}
              />
            </Flex>
          )}
          {!openSearch && (
            <Flex size="0.1" direction="row" justify="flex-end">
              <BsSearch onClick={() => setOpenSearch(true)} />
            </Flex>
          )}
          <Flex size="0.1" direction="row" justify="flex-end" pl={4} p={0}>
            <FaFilter onClick={() => setExpandFilter(!expandFilter)} />
          </Flex>
          {expandFilter && (
            <Modal
              open={expandFilter}
              onClose={() => {
                setExpandFilter(!expandFilter);
              }}
              heading="Filter"
              zIndex={1000}
            >
              <Box style={{ zIndex: 10 }}>
                <Flex direction="column" size="5" justify="space-between">
                  <CollapsibleFilterWrapper>
                    <CollapsibleFilters
                      changeHandler={_changeHandler}
                      filterOptions={
                        match.url.startsWith('/institution') ? INSTITUTION_PORTAL_FILTER_OPTIONS : FILTER_OPTIONS
                      }
                      filterState={state}
                      searchName={searchTerm}
                      setSearchName={setSearchTerm}
                    />
                  </CollapsibleFilterWrapper>
                  <Flex direction="row" size="5" justify="space-between" pt={4}>
                    <Button
                      type="button"
                      text="Clear"
                      p={0}
                      width="40px"
                      onClick={() => {
                        setFilterState({ ...initialFilterState });
                        setExpandFilter(!expandFilter);
                      }}
                    />
                    <Button
                      type="button"
                      p={0}
                      width="40px"
                      text="Apply"
                      onClick={() => {
                        setFilterState({ ...filterState, ...state });
                        setExpandFilter(!expandFilter);
                      }}
                    />
                  </Flex>
                </Flex>
              </Box>
            </Modal>
          )}
        </Flex>
      )}
      <Flex direction="row" size="1" justify="flex-end" alignItems="center">
        {isWeb && !match.url.includes('/manage/institutions') && <NotificationsDropDown />}
        {!isWeb && !match.url.includes('/manage/institutions') && (
          <Flex direction="row" size="1" justify="flex-end" alignItems="center">
            <NotificationsIcon notificationsURL={`${url()}/notifications`} />
          </Flex>
        )}
        {isWeb && (
          <AuthConsumer>
            {state => {
              return (
                <Fragment>
                  <Wrapper>
                    <PortalDropdown />
                  </Wrapper>
                </Fragment>
              );
            }}
          </AuthConsumer>
        )}
      </Flex>
    </HeaderCont>
  );
};

Header.defaultProps = {
  p: 4,
  bg: 'primary',
  color: 'white',
  height: '70px',
  width: '100%',
};

export default withRouter(Header);
